import type { CharacterDef } from "../economy/characters";
import type { AvatarDisplay } from "../wallet/avatarResolver";
import { PROFILE_ITEMS_PER_PAGE } from "./profilePagination";

export type ProfileFilter = "all" | "wallet";
export type ProfileViewMode = "browse" | "grid";

export const PROFILE_WEAR_LABEL = "WEAR";
export const PROFILE_WEARING_LABEL = "WEARING";

export interface ProfileSkinVisibleItem {
  kind: "skin";
  targetId: string;
  imagePath: string;
  color: string;
  icon: string;
  equipped: boolean;
  rarity: CharacterDef["rarity"];
  skin: CharacterDef;
}

export interface ProfileNftVisibleItem {
  kind: "nft";
  targetId: string;
  imagePath: string;
  color: string;
  icon: string;
  equipped: boolean;
  rarity: AvatarDisplay["rarity"];
  tokenId: number;
  source: AvatarDisplay["source"];
  vaultAddress?: string;
  avatar: AvatarDisplay;
}

export type ProfileVisibleItem = ProfileSkinVisibleItem | ProfileNftVisibleItem;

function normalizeIndex(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.max(0, Math.floor(value));
}

export function buildProfileVisibleItems(
  filter: ProfileFilter,
  ownedSkins: readonly CharacterDef[],
  nftAvatars: readonly AvatarDisplay[],
  equippedSkinId: string | null = null,
): ProfileVisibleItem[] {
  const items: ProfileVisibleItem[] = [];

  if (filter === "all") {
    for (const skin of ownedSkins) {
      items.push({
        kind: "skin",
        targetId: skin.id,
        imagePath: skin.imageSmallPath,
        color: skin.color,
        icon: skin.icon,
        equipped: skin.id === equippedSkinId,
        rarity: skin.rarity,
        skin,
      });
    }
  }

  for (const avatar of nftAvatars) {
    items.push({
      kind: "nft",
      targetId: avatar.avatarId,
      imagePath: avatar.imagePath,
      color: avatar.color,
      icon: `${avatar.tokenId % 10}`,
      equipped: avatar.isEquipped,
      rarity: avatar.rarity,
      tokenId: avatar.tokenId,
      source: avatar.source,
      ...(avatar.vaultAddress != null ? { vaultAddress: avatar.vaultAddress } : {}),
      avatar,
    });
  }

  return items;
}

export function resolveProfileVisibleSelectionIndex(
  viewMode: ProfileViewMode,
  profilePage: number,
  profileBrowseIndex: number,
  slotIndex: number,
): number {
  if (viewMode === "browse") {
    return normalizeIndex(profileBrowseIndex);
  }
  return normalizeIndex(profilePage) * PROFILE_ITEMS_PER_PAGE + normalizeIndex(slotIndex);
}

export function getSelectedProfileVisibleItem(
  filter: ProfileFilter,
  ownedSkins: readonly CharacterDef[],
  nftAvatars: readonly AvatarDisplay[],
  equippedSkinId: string | null,
  viewMode: ProfileViewMode,
  profilePage: number,
  profileBrowseIndex: number,
  slotIndex: number,
): ProfileVisibleItem | null {
  const items = buildProfileVisibleItems(filter, ownedSkins, nftAvatars, equippedSkinId);
  const selectedIndex = resolveProfileVisibleSelectionIndex(
    viewMode,
    profilePage,
    profileBrowseIndex,
    slotIndex,
  );
  return items[selectedIndex] ?? null;
}
